import React, { useEffect, useState, createContext, useContext } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { api, absUrl } from "../../lib/api";
import Footer from "./Footer";
import FloatingActions from "./FloatingActions";

export const SiteContext = createContext({ site: null });
export const useSite = () => useContext(SiteContext);

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/packages", label: "Packages" },
  { to: "/menus", label: "Menus" },
  { to: "/special-setups", label: "Setups" },
  { to: "/gallery", label: "Gallery" },
  { to: "/testimonials", label: "Reviews" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout() {
  const [site, setSite] = useState(null);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const { pathname } = useLocation();

  useEffect(() => {
    api.get("/public/site").then((r) => setSite(r.data)).catch(() => {}).finally(() => {
      setTimeout(() => setLoading(false), 600);
    });
  }, []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); setOpen(false); }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <SiteContext.Provider value={{ site }}>
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
            className="fixed inset-0 z-[100] bg-navy-900 flex items-center justify-center"
            data-testid="loading-screen"
          >
            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="w-28 h-28 rounded-full border border-gold/40 animate-spin-slow" />
                <div className="absolute inset-2 rounded-full border border-gold/20 animate-spin-slow" style={{ animationDirection: "reverse", animationDuration: "8s" }} />
                <div className="absolute inset-0 flex items-center justify-center serif text-shimmer text-3xl">SB</div>
              </div>
              <div className="mt-6 text-gold tracking-royal text-xs uppercase">Shree Balaji Caterers</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled || open ? "glass-navy py-3" : "bg-transparent py-5"
        }`}
        data-testid="public-header"
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            {site?.logo_url && (
              <img src={absUrl(site.logo_url)} alt="Balaji Caterers" className="w-11 h-11 rounded-full border border-gold/60 object-cover" />
            )}
            <div className="leading-tight whitespace-nowrap">
              <div className="serif text-ivory text-lg xl:text-xl tracking-tight">Shree Balaji</div>
              <div className="text-gold/80 tracking-royal text-[9px] uppercase">Caterers · Vadodara</div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-6">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`text-[10px] tracking-[0.2em] uppercase font-semibold transition-colors ${
                  pathname === n.to ? "text-gold" : "text-ivory/80 hover:text-gold"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-2">
            <a href={`tel:${site?.phone || "+919328797924"}`} className="btn-outline !py-2 !px-4 !text-[10px]" data-testid="header-call-btn">
              <Phone size={14} /> Call
            </a>
            <Link to="/contact" className="btn-gold !py-2 !px-4 !text-[10px]" data-testid="header-inquire-btn">Inquire</Link>
          </div>

          <button onClick={() => setOpen(!open)} className="xl:hidden text-gold p-2" data-testid="mobile-menu-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="xl:hidden overflow-hidden border-t border-gold/20 mt-3"
            >
              <div className="px-4 py-6 flex flex-col gap-3 max-w-7xl mx-auto">
                {NAV.map((n) => (
                  <Link
                    key={n.to} to={n.to}
                    className={`text-sm tracking-[0.2em] uppercase py-2 ${pathname === n.to ? "text-gold" : "text-ivory/80"}`}
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  >
                    {n.label}
                  </Link>
                ))}
                <a href={`tel:${site?.phone || "+919328797924"}`} className="btn-gold mt-2 justify-center" data-testid="mobile-call-btn">
                  <Phone size={14} /> Call · {site?.phone || "+91 9328797924"}
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="pt-0">
        <Outlet />
      </main>

      <Footer site={site} />
      <FloatingActions site={site} />
      <BackToTop />
    </SiteContext.Provider>
  );
}

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const f = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);
  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-6 left-6 z-40 w-11 h-11 rounded-full bg-navy-900 border border-gold/40 text-gold flex items-center justify-center hover:bg-gold hover:text-navy-900 transition-colors"
      data-testid="back-to-top"
    >
      <ChevronUp size={18} />
    </button>
  );
}
